import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { getAppToken, resetAppToken } from '@/app/apis';
import { useFetch, useGlobalModal } from '@/app/hooks';
import { useStore } from '@/app/providers/store';

interface UseViduTokenOptions {
  onResetTokenSuccess?: () => void;
}

/**
 * Vidu Token 获取 / 重置 / 弹窗状态控制 Hook
 *
 * 调用方负责渲染触发器（按钮、菜单项等），并通过 `requestToken` 主动拉取，
 * 然后将返回值传给 `<GetTokenDialog />` 渲染弹窗。
 */
export const useViduToken = ({ onResetTokenSuccess }: UseViduTokenOptions = {}) => {
  const t = useTranslations();
  const { open: openLoginDialog } = useGlobalModal('loginDialog');
  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');

  const [open, setOpen] = useState<boolean>(false);
  const [viduToken, setViduToken] = useState<string>('');
  const [token, setToken] = useState<string>('');

  const { userInfo, isUserInfoInitialized, activeClaw } = useStore((state) => ({
    userInfo: state.userInfo,
    isUserInfoInitialized: state.isUserInfoInitialized,
    activeClaw: state.activeClaw,
  }));

  useEffect(() => {
    setToken(viduToken);
  }, [viduToken]);

  const { run: runGetAppToken } = useFetch(getAppToken, {
    manual: true,
    onSuccess: ({ data }) => {
      setOpen(true);
      setViduToken(data?.token ?? '');
    },
  });

  const { run: runResetAppToken } = useFetch(resetAppToken, {
    manual: true,
    onSuccess: ({ error, data }) => {
      if (error) {
        return;
      }
      setViduToken(data?.token ?? '');
      onResetTokenSuccess?.();
    },
  });

  const requestToken = () => {
    if (!isUserInfoInitialized) {
      return;
    }
    if (!userInfo) {
      openLoginDialog();
      return;
    }
    runGetAppToken({
      path: {
        id: userInfo?.id ?? '',
      },
    });
  };

  const handleReset = () => {
    openConfirmDialog({
      title: t('popup.dialog.resetToken.title'),
      description: activeClaw ? t('popup.dialog.resetToken.clawDescription') : t('popup.dialog.resetToken.description'),
      secondaryButtonText: t('popup.dialog.resetToken.cancel'),
      primaryButtonText: t('popup.dialog.resetToken.confirm'),
      onPrimaryAction: () => {
        setViduToken('');
        runResetAppToken({
          body: {},
        });
      },
    });
  };

  return {
    open,
    setOpen,
    token,
    requestToken,
    handleReset,
  };
};
